// src/stores/performanceReviewStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import svc from "@/services/hrm/performanceReviewService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePerformanceReviewStore = defineStore(
  "performanceReview",
  () => {
    const items = ref([]);
    const item = ref(null);
    const loadingPdfId = ref(null);
    const loading = ref(false);
    const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
    const filters = ref({ search: "", status: "", per_page: 25, page: 1 });

    async function fetchAll(extra = {}) {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_PERFORMANCE))) {
        items.value = [];
        pagination.value = { total: 0, per_page: 25, current_page: 1 };
        return;
      }

      loading.value = true;
      try {
        const { data } = await svc.getAll({ ...filters.value, ...extra });
        items.value = data.data ?? data;
        if (data.meta) {
          pagination.value = {
            total: data.meta.total,
            per_page: data.meta.per_page,
            current_page: data.meta.current_page,
          };
        }
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to fetch reviews", "error");
      } finally {
        loading.value = false;
      }
    }
    async function fetchOne(id) {
      loading.value = true;
      try {
        const { data } = await svc.get(id);
        item.value = data;
        return data;
      } finally {
        loading.value = false;
      }
    }
    async function create(payload) {
      loading.value = true;
      try {
        const { data } = await svc.create(payload);
        toast("Review created");
        await fetchAll();
        return data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to create", "error");
        throw e;
      } finally {
        loading.value = false;
      }
    }

    async function update(id, payload) {
      loading.value = true;
      try {
        const { data } = await svc.update(id, payload);
        toast("Review updated");
        await fetchAll();
        return data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to update", "error");
        throw e;
      } finally {
        loading.value = false;
      }
    }

    async function remove(id) {
      loading.value = true;
      try {
        await svc.delete(id);
        toast("Review deleted");
        await fetchAll();
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to delete", "error");
        throw e;
      } finally {
        loading.value = false;
      }
    }
    function saveBlob(blob, fallbackName = "review.pdf", res) {
      // Try to read filename from header
      let filename = fallbackName;
      const hdr =
        res?.headers?.["content-disposition"] ||
        res?.headers?.get?.("content-disposition");
      if (hdr) {
        const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(hdr);
        const raw = decodeURIComponent(m?.[1] || m?.[2] || "");
        if (raw) filename = raw;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    async function downloadPdf(id) {
      loadingPdfId.value = id;
      try {
        const res = await svc.downloadPdf(id);
        const blob = new Blob([res.data], { type: "application/pdf" });
        saveBlob(blob, `performance-review-${id}.pdf`, res);
        toast("PDF downloaded");
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to download PDF", "error");
        throw e;
      } finally {
        loadingPdfId.value = null;
      }
    }
    return {
      items,
      item,
      loading,
      pagination,
      filters,
      fetchAll,
      fetchOne,
      create,
      update,
      remove,
      loadingPdfId,
      downloadPdf,
    };
  }
);
